// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import type { SiteTextDto } from "@Front/Api/LeCoffreApi/Public/SiteTexts/SiteTexts";
import type { ListSiteTextsParams, CreateSiteTextPayload, UpdateSiteTextPayload } from "./SiteTexts";
import { buildEmptyPayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface SiteTextsAdminApiInstance {
	list(params?: ListSiteTextsParams): Promise<SiteTextDto[]>;
	create(payload: CreateSiteTextPayload): Promise<SiteTextDto>;
	update(payload: UpdateSiteTextPayload): Promise<SiteTextDto>;
	publish(uid: string): Promise<SiteTextDto>;
	archive(uid: string): Promise<SiteTextDto>;
}

// Module-level singleton instance
let instance: SiteTextsAdminApiInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

function buildListUrl(baseSuperAdmin: IBaseSuperAdmin, params: ListSiteTextsParams): URL {
	const url = new URL(`${baseSuperAdmin.namespaceUrl}/site-texts`);

	if (params.scope) {
		url.searchParams.set("scope", params.scope);
	}

	if (params.locale) {
		url.searchParams.set("locale", params.locale);
	}

	if (params.status) {
		url.searchParams.set("status", params.status);
	}

	if (params.search) {
		url.searchParams.set("search", params.search);
	}

	return url;
}

async function siteTextsList(baseSuperAdmin: IBaseSuperAdmin, params: ListSiteTextsParams = {}): Promise<SiteTextDto[]> {
	const url = buildListUrl(baseSuperAdmin, params);
	try {
		return await baseSuperAdmin.getRequest<SiteTextDto[]>(url);
	} catch (error) {
		LoggerService.error("❌ [SiteTextsAdminApi] Failed to list site texts", { error, params });
		throw error;
	}
}

async function siteTextsCreate(baseSuperAdmin: IBaseSuperAdmin, payload: CreateSiteTextPayload): Promise<SiteTextDto> {
	const url = new URL(`${baseSuperAdmin.namespaceUrl}/site-texts`);
	try {
		return await baseSuperAdmin.postRequest<SiteTextDto>(url, payload);
	} catch (error) {
		LoggerService.error("❌ [SiteTextsAdminApi] Failed to create site text", { error, payload });
		throw error;
	}
}

async function siteTextsUpdate(baseSuperAdmin: IBaseSuperAdmin, payload: UpdateSiteTextPayload): Promise<SiteTextDto> {
	const { uid: siteTextUid, ...data } = payload;
	const url = new URL(`${baseSuperAdmin.namespaceUrl}/site-texts/${siteTextUid}`);
	try {
		return await baseSuperAdmin.putRequest<SiteTextDto>(url, data);
	} catch (error) {
		LoggerService.error("❌ [SiteTextsAdminApi] Failed to update site text", { error, payload });
		throw error;
	}
}

async function siteTextsPublish(baseSuperAdmin: IBaseSuperAdmin, uid: string): Promise<SiteTextDto> {
	const url = new URL(`${baseSuperAdmin.namespaceUrl}/site-texts/${uid}/publish`);
	try {
		return await baseSuperAdmin.postRequest<SiteTextDto>(url, buildEmptyPayload());
	} catch (error) {
		LoggerService.error("❌ [SiteTextsAdminApi] Failed to publish site text", { error, uid });
		throw error;
	}
}

async function siteTextsArchive(baseSuperAdmin: IBaseSuperAdmin, uid: string): Promise<SiteTextDto> {
	const url = new URL(`${baseSuperAdmin.namespaceUrl}/site-texts/${uid}/archive`);
	try {
		return await baseSuperAdmin.postRequest<SiteTextDto>(url, buildEmptyPayload());
	} catch (error) {
		LoggerService.error("❌ [SiteTextsAdminApi] Failed to archive site text", { error, uid });
		throw error;
	}
}

function createSiteTextsAdminApi(): SiteTextsAdminApiInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	return {
		list: (params?: ListSiteTextsParams) => siteTextsList(baseSuperAdmin, params ?? {}),
		create: (payload: CreateSiteTextPayload) => siteTextsCreate(baseSuperAdmin, payload),
		update: (payload: UpdateSiteTextPayload) => siteTextsUpdate(baseSuperAdmin, payload),
		publish: (uid: string) => siteTextsPublish(baseSuperAdmin, uid),
		archive: (uid: string) => siteTextsArchive(baseSuperAdmin, uid),
	};
}

export function getSiteTextsAdminApiInstance(): SiteTextsAdminApiInstance {
	if (!instance) {
		instance = createSiteTextsAdminApi();
	}
	return instance;
}
